/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Icon, IconButton, SliderModal, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Pressable, ScrollView } from 'native-base';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dimensions } from 'react-native';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import * as zod from 'zod';

import { SignedInRootStackParamList } from '../../../routes/types';
import { customerItemState } from '../../../store/customer/atom';
import {
  useDirectTreasuryActions,
  useFixedIncomeActions,
  useFundsActions,
  useStructuredOperationsActions,
} from '../../../store/product/actions';
import {
  directTreasuryOffersAllState,
  directTreasuryOffersByFilterParamsState,
  fixedIncomeOffersAllState,
  fixedIncomeOffersByFilterParamsState,
  fundOffersAllState,
  fundOffersByFilterParamsState,
  structuredOperationsOffersAllState,
  structuredOperationsOffersParamsState,
} from '../../../store/product/atom';
import { dataOptionsInterface } from '../Product/constants';

import { Footer } from './components/Footer';
import { InputApplicationRange } from './components/InputApplicationRange';
import { MultiSlider } from './components/MultiSlider';
import { Section } from './components/Section';
import * as product from './data';

type OptionSelectedType = { [key: string]: string[] };

export interface SelectOptionsProps {
  value: OptionSelectedType;
  isSingle: boolean;
}

const sliderModalWidth = 0.23;

export const applicationRangeDefault = { min: 0, max: 1000000 };
export const daysRangeDefault = { min: 0, max: 36500 };

type ObjectRange = typeof daysRangeDefault;

export const objectIsEquals = (state: ObjectRange, defaultState: ObjectRange) => {
  return state.min === defaultState.min && state.max === defaultState.max;
};

const removeEmptyArrays = (obj: OptionSelectedType) => {
  for (const prop in obj) {
    if (Array.isArray(obj[prop]) && obj[prop].length === 0) {
      delete obj[prop];
    }
  }

  return obj;
};

const applicationRangeSchema = zod.object({
  minAppliedAmount: zod.string(),
  maxAppliedAmount: zod.string(),
});

export type ApplicationRangeFormData = zod.infer<typeof applicationRangeSchema>;

const { height } = Dimensions.get('window');

export const NewApplicationFilter = () => {
  const navigation = useNavigation();

  const { updateFixedIncomeOffersByFilterParams } = useFixedIncomeActions();
  const { updateFundOffersByFilterParams } = useFundsActions();
  const { updateStructuredOperationsOffersParams } = useStructuredOperationsActions();
  const { updateDirectTreasuryOffersByFilterParams } = useDirectTreasuryActions();

  const resetFixedIncomeOffersAllState = useResetRecoilState(fixedIncomeOffersAllState);
  const resetFundOffersAllState = useResetRecoilState(fundOffersAllState);
  const resetStructuredOperationsOffersAllState = useResetRecoilState(
    structuredOperationsOffersAllState
  );
  const resetDirectTreasuryOffersAllState = useResetRecoilState(directTreasuryOffersAllState);

  const resetFixedIncomeOffersByFilterParams = useResetRecoilState(
    fixedIncomeOffersByFilterParamsState
  );
  const resetFundOffersByFilterParamsState = useResetRecoilState(fundOffersByFilterParamsState);
  const resetStructuredOperationsOffersParamsState = useResetRecoilState(
    structuredOperationsOffersParamsState
  );
  const resetDirectTreasuryOffersByFilterParamsState = useResetRecoilState(
    directTreasuryOffersByFilterParamsState
  );

  const fixedIncomeOffersParams = useRecoilValue(fixedIncomeOffersByFilterParamsState);
  const fundOffersParams = useRecoilValue(fundOffersByFilterParamsState);
  const structuredOperationsOffersParams = useRecoilValue(structuredOperationsOffersParamsState);
  const directTreasuryOffersParams = useRecoilValue(directTreasuryOffersByFilterParamsState);

  const [selectedOptions, setSelectedOptions] = useState({} as OptionSelectedType);
  const [resetKey, setResetKey] = useState(false);

  const customerItem = useRecoilValue(customerItemState);

  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'NewApplicationFilter'>>();

  const maximumRedemptionDays = params?.maturityDay || daysRangeDefault.max;

  const currentProductData = product[params.type];
  const isFixedIncome = params.type === 'FixedIncome';
  const isFund = params.type === 'Fund';
  const isStructuredOperation = params.type === 'StructuredOperations';
  const isDirectTreasury = params.type === 'DirectTreasury';

  const currentFixedIncomeApplicationAmountParams = {
    min: fixedIncomeOffersParams.applicationMinimumAmount || applicationRangeDefault.min,
    max: fixedIncomeOffersParams.applicationMaximumAmount || applicationRangeDefault.max,
  };
  const currentFundApplicationAmountParams = {
    min: fundOffersParams.applicationMinimumAmount || applicationRangeDefault.min,
    max: fundOffersParams.applicationMaximumAmount || applicationRangeDefault.max,
  };
  const currentStructuredOperationsAmountParams = {
    min: structuredOperationsOffersParams.applicationMinimumAmount || applicationRangeDefault.min,
    max: structuredOperationsOffersParams.applicationMaximumAmount || applicationRangeDefault.max,
  };
  const currentDirectTreasuryApplicationAmountParams = {
    min: directTreasuryOffersParams.applicationMinimumAmount || applicationRangeDefault.min,
    max: directTreasuryOffersParams.applicationMaximumAmount || applicationRangeDefault.max,
  };

  const currentFixedIncomeDaysParams = {
    min: fixedIncomeOffersParams.minimumDaysToRedemption || daysRangeDefault.min,
    max: fixedIncomeOffersParams.maximumDaysToRedemption || maximumRedemptionDays,
  };
  const currentFundDaysParams = {
    min: fundOffersParams.minimumRedemptionDays || daysRangeDefault.min,
    max: fundOffersParams.maximumRedemptionDays || maximumRedemptionDays,
  };
  const currentStructuredOperationsDaysParams = {
    min: structuredOperationsOffersParams.minimumDaysToRedemption || daysRangeDefault.min,
    max: structuredOperationsOffersParams.maximumDaysToRedemption || maximumRedemptionDays,
  };
  const currentDirectTreasuryDaysParams = {
    min: directTreasuryOffersParams.minimumDaysToRedemption || daysRangeDefault.min,
    max: directTreasuryOffersParams.maximumDaysToRedemption || maximumRedemptionDays,
  };

  const currentApplicationRanges = {
    FixedIncome: currentFixedIncomeApplicationAmountParams,
    Fund: currentFundApplicationAmountParams,
    StructuredOperations: currentStructuredOperationsAmountParams,
    DirectTreasury: currentDirectTreasuryApplicationAmountParams,
  };

  const formEdit = useForm({
    resolver: zodResolver(applicationRangeSchema),
    mode: 'onChange',
    defaultValues: {
      minAppliedAmount: Utils.Mask.Money(currentApplicationRanges[params.type].min),
      maxAppliedAmount: Utils.Mask.Money(currentApplicationRanges[params.type].max),
    },
  });

  const { handleSubmit, clearErrors, resetField, setValue } = formEdit;

  const currentDaysRanges = {
    FixedIncome: currentFixedIncomeDaysParams,
    Fund: currentFundDaysParams,
    StructuredOperations: currentStructuredOperationsDaysParams,
    DirectTreasury: currentDirectTreasuryDaysParams,
  };

  const [daysRange, setDaysRange] = useState(currentDaysRanges[params.type]);

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleSelectOption = ({ isSingle, value }: SelectOptionsProps) => {
    setSelectedOptions((state) => {
      const fieldName = Object.keys(value)[0];

      if (state[fieldName] && state.hasOwnProperty(fieldName)) {
        const valueToCheck = value[fieldName][0];

        if (state[fieldName]) {
          const containsValue = !!state[fieldName] && state[fieldName]?.includes(valueToCheck);

          if (containsValue) {
            return {
              ...state,
              [fieldName]: state[fieldName].filter((item: string) => item !== valueToCheck),
            };
          }
        }
        if (isSingle) {
          return { ...state, ...value };
        }

        return { ...state, [fieldName]: [...state[fieldName], ...value[fieldName]] };
      } else {
        return { ...state, ...value };
      }
    });
  };

  const selectedValues = Object.values(selectedOptions).flat() as string[];

  const daysRangeIsDefaultValue = objectIsEquals(daysRange, {
    min: daysRangeDefault.min,
    max: params?.maturityDay || daysRangeDefault.max,
  });

  const daysRangeIsChanged = objectIsEquals(daysRange, currentDaysRanges[params.type]);
  const buttonApplyIsDisabled = selectedValues.length === 0 && daysRangeIsChanged;
  const buttonCleanIsDisabled = selectedValues.length === 0 && daysRangeIsDefaultValue;

  const cleanFilters = () => {
    setResetKey(true);

    setSelectedOptions({});
    resetField('maxAppliedAmount');
    resetField('minAppliedAmount');
    clearErrors('maxAppliedAmount');
    clearErrors('minAppliedAmount');
    setValue('maxAppliedAmount', Utils.Mask.Money(applicationRangeDefault.max));
    setValue('minAppliedAmount', Utils.Mask.Money(applicationRangeDefault.min));

    setDaysRange(daysRangeDefault);
    if (isFixedIncome) {
      resetFixedIncomeOffersAllState();
      resetFixedIncomeOffersByFilterParams();
      updateFixedIncomeOffersByFilterParams({ page: 1, limit: 40 });
    }

    if (isFund) {
      resetFundOffersAllState();
      resetFundOffersByFilterParamsState();
      updateFundOffersByFilterParams({
        page: 1,
        limit: 40,
        checkingAccountNumber: customerItem.current.checkingAccountNumber,
      });
    }

    if (isStructuredOperation) {
      resetStructuredOperationsOffersAllState();
      resetStructuredOperationsOffersParamsState();
      updateStructuredOperationsOffersParams({ page: 1, limit: 40 });
    }

    if (isDirectTreasury) {
      resetDirectTreasuryOffersAllState();
      resetDirectTreasuryOffersByFilterParamsState();
      updateDirectTreasuryOffersByFilterParams({ page: 1, limit: 40 });
    }

    setTimeout(() => {
      setResetKey(false);
    }, 3000);
  };

  const findPropertiesWithIsSingle = (
    data: OptionSelectedType,
    optionsObject: typeof currentProductData
  ) => {
    const matchedProperties = {};

    for (const prop in optionsObject) {
      if (data.hasOwnProperty(prop)) {
        if (optionsObject[prop].hasOwnProperty('isSingle')) {
          if (optionsObject[prop].isSingle && selectedOptions[prop][0]) {
            if (optionsObject[prop].type === 'boolean') {
              matchedProperties[prop] = selectedOptions[prop][0].includes('true');
            }

            if (optionsObject[prop].type === 'number') {
              const selectedOptionValue = selectedOptions[prop][0].split(':')[1].trim();
              matchedProperties[prop] = Number(selectedOptionValue);
            }
          }
        }
        if (selectedOptions[prop] && selectedOptions[prop].length > 0) {
          matchedProperties[prop] = selectedOptions[prop].map((item: string) => {
            if (typeof item === 'string' && item.includes(':')) {
              return item.split(':')[1].trim();
            }

            return item;
          });
        }
      }
    }

    return matchedProperties as {
      [key: string]: boolean | number;
    };
  };

  const handleApplyFilters = ({ maxAppliedAmount, minAppliedAmount }: ApplicationRangeFormData) => {
    const data = removeEmptyArrays(selectedOptions);
    const result = findPropertiesWithIsSingle(data, currentProductData);
    const fixedIncomeApplicationAmount = {
      applicationMinimumAmount: Utils.Unmask.Money(minAppliedAmount),
      applicationMaximumAmount: Utils.Unmask.Money(maxAppliedAmount),
    };

    const fixedIncomeDaysToRedemption = {
      minimumDaysToRedemption: daysRange.min,
      maximumDaysToRedemption: daysRange.max,
    };

    const fundApplicationAmount = {
      applicationMinimumAmount: Utils.Unmask.Money(minAppliedAmount),
      applicationMaximumAmount: Utils.Unmask.Money(maxAppliedAmount),
    };

    const fundDaysToRedemption = {
      minimumRedemptionDays: daysRange.min,
      maximumRedemptionDays: daysRange.max,
    };

    const structuredOperationsApplicationAmount = {
      applicationMinimumAmount: Utils.Unmask.Money(minAppliedAmount),
      applicationMaximumAmount: Utils.Unmask.Money(maxAppliedAmount),
    };

    const structuredOperationsDaysToRedemption = {
      minimumDaysToRedemption: daysRange.min,
      maximumDaysToRedemption: daysRange.max,
    };

    const directTreasuryApplicationAmount = {
      applicationMinimumAmount: Utils.Unmask.Money(minAppliedAmount),
      applicationMaximumAmount: Utils.Unmask.Money(maxAppliedAmount),
    };

    const directTreasuryDaysToRedemption = {
      minimumDaysToRedemption: daysRange.min,
      maximumDaysToRedemption: daysRange.max,
    };

    const applicationAmount = {
      FixedIncome: fixedIncomeApplicationAmount,
      Fund: fundApplicationAmount,
      StructuredOperations: structuredOperationsApplicationAmount,
      DirectTreasury: directTreasuryApplicationAmount,
    };
    const daysToRedemption = {
      FixedIncome: fixedIncomeDaysToRedemption,
      Fund: fundDaysToRedemption,
      StructuredOperations: structuredOperationsDaysToRedemption,
      DirectTreasury: directTreasuryDaysToRedemption,
    };

    const sortItems = {
      FixedIncome: fixedIncomeOffersParams.sortItems,
      Fund: fundOffersParams.sortItems,
      StructuredOperations: structuredOperationsOffersParams.sortItems,
      DirectTreasury: directTreasuryOffersParams.sortItems,
    };

    const paramsData = {
      ...data,
      ...result,
      ...applicationAmount[params.type],
      ...daysToRedemption[params.type],
      sortItems: sortItems[params.type],
    };

    if (isFixedIncome) {
      resetFixedIncomeOffersAllState();
      resetFixedIncomeOffersByFilterParams();
      updateFixedIncomeOffersByFilterParams(paramsData);
    }
    if (isFund) {
      resetFundOffersAllState();
      resetFundOffersByFilterParamsState();
      updateFundOffersByFilterParams(paramsData);
    }
    if (isStructuredOperation) {
      resetStructuredOperationsOffersAllState();
      resetStructuredOperationsOffersParamsState();
      updateStructuredOperationsOffersParams(paramsData);
    }
    if (isDirectTreasury) {
      resetDirectTreasuryOffersAllState();
      resetDirectTreasuryOffersByFilterParamsState();
      updateDirectTreasuryOffersByFilterParams(paramsData);
    }

    goBack();
  };

  useEffect(() => {
    const {
      minimumDaysToRedemption: minimumDaysToRedemptionFixedIncomeOffersParams,
      maximumDaysToRedemption: maximumDaysToRedemptionFixedIncomeOffersParams,
      applicationMinimumAmount: applicationMinimumAmountFixedIncomeOffersParams,
      applicationMaximumAmount: applicationMaximumAmountFixedIncomeOffersParams,
      ...restFixedIncomeOffersParams
    } = fixedIncomeOffersParams;
    const {
      minimumRedemptionDays: minimumDaysToRedemptionFundOffersParams,
      maximumRedemptionDays: maximumDaysToRedemptionFundOffersParams,
      applicationMaximumAmount: applicationMaximumAmountFundOffersParams,
      applicationMinimumAmount: applicationMinimumAmountFundOffersParams,
      ...restFundOffersParams
    } = fundOffersParams;
    const {
      minimumDaysToRedemption: minimumDaysToRedemptionStructuredOperationsOffersParams,
      maximumDaysToRedemption: maximumDaysToRedemptionStructuredOperationsOffersParams,
      applicationMaximumAmount: applicationMaximumAmountStructuredOperationsOffersParams,
      applicationMinimumAmount: applicationMinimumAmountStructuredOperationsOffersParams,
      ...restStructuredOperationsOffersParams
    } = structuredOperationsOffersParams;
    const {
      maximumDaysToRedemption: minimumDaysToRedemptionDirectTreasuryOffersParams,
      minimumDaysToRedemption: maximumDaysToRedemptionDirectTreasuryOffersParams,
      applicationMaximumAmount: applicationMaximumAmountDirectTreasuryOffersParams,
      applicationMinimumAmount: applicationMinimumAmountDirectTreasuryOffersParams,
      ...restDirectTreasuryOffersParams
    } = directTreasuryOffersParams;
    const hasStrategy =
      restStructuredOperationsOffersParams?.strategy &&
      restStructuredOperationsOffersParams?.strategy?.length > 0;
    const hasDirection =
      restStructuredOperationsOffersParams?.direction &&
      restStructuredOperationsOffersParams?.direction?.length > 0;
    const hasIndexer =
      restDirectTreasuryOffersParams?.indexer &&
      restDirectTreasuryOffersParams?.indexer?.length > 0;
    const hasRiskRatingLevel =
      restDirectTreasuryOffersParams?.riskRatingLevel &&
      restDirectTreasuryOffersParams?.riskRatingLevel?.length > 0;

    const currentParamsData = {
      FixedIncome: restFixedIncomeOffersParams,
      Fund: restFundOffersParams,
      StructuredOperations: {
        ...restStructuredOperationsOffersParams,
        ...(hasStrategy && {
          strategy: restStructuredOperationsOffersParams?.strategy?.map(
            (item) => `strategy:${item}`
          ),
        }),
        ...(hasDirection && {
          direction: restStructuredOperationsOffersParams?.direction?.map(
            (item) => `direction:${item}`
          ),
        }),
      },
      DirectTreasury: {
        ...restDirectTreasuryOffersParams,
        ...(hasIndexer && {
          indexer: restDirectTreasuryOffersParams?.indexer?.map((item) => `indexer:${item}`),
        }),
        ...(hasRiskRatingLevel && {
          riskRatingLevel: restDirectTreasuryOffersParams?.riskRatingLevel?.map(
            (item) => `riskRatingLevel:${item}`
          ),
        }),
      },
    };
    const currentParams = currentParamsData[params.type];
    const existingPropertiesWithValues = product.isOnlyProperties
      .filter((prop) => prop in currentParams)
      .map((prop) => ({ [prop]: currentParams[prop] }));

    const formattedProperties = existingPropertiesWithValues.reduce((result, obj) => {
      const propertyName = Object.keys(obj)[0];
      const propertyValue = obj[propertyName];

      const formatted = { [propertyName]: [`${propertyName}:${propertyValue}`] };

      return { ...result, ...formatted };
    }, {});

    const { page, limit, sortItems, ...rest } = currentParams;

    const data = { ...rest, ...formattedProperties } as unknown as OptionSelectedType;
    setSelectedOptions(data);
  }, [
    fixedIncomeOffersParams,
    fundOffersParams,
    structuredOperationsOffersParams,
    directTreasuryOffersParams,
    params.type,
  ]);

  const sliderComponentsOrder = {
    FixedIncome: (
      <VStack w={'100%'}>
        <FormProvider {...formEdit}>
          <InputApplicationRange />
        </FormProvider>
        <MultiSlider
          type="days"
          title="Quando você pretende resgatar"
          resetKey={resetKey}
          currentValue={daysRange}
          min={daysRangeDefault.min}
          max={maximumRedemptionDays}
          steps={1}
          onValueChange={(range) =>
            setDaysRange({ max: Number(range.max), min: Number(range.min) })
          }
        />
      </VStack>
    ),
    Fund: (
      <VStack w={'100%'}>
        <FormProvider {...formEdit}>
          <InputApplicationRange />
        </FormProvider>
        <MultiSlider
          type="days"
          title="Resgate antecipado"
          resetKey={resetKey}
          currentValue={daysRange}
          min={daysRangeDefault.min}
          max={maximumRedemptionDays}
          steps={1}
          onValueChange={(range) =>
            setDaysRange({ max: Number(range.max), min: Number(range.min) })
          }
        />
      </VStack>
    ),
    StructuredOperations: (
      <VStack w={'100%'}>
        <FormProvider {...formEdit}>
          <InputApplicationRange label="Valor de aplicação" />
        </FormProvider>
        <MultiSlider
          type="days"
          title="Escolha o vencimento"
          resetKey={resetKey}
          currentValue={daysRange}
          min={daysRangeDefault.min}
          max={maximumRedemptionDays}
          steps={1}
          onValueChange={(range) =>
            setDaysRange({ max: Number(range.max), min: Number(range.min) })
          }
        />
      </VStack>
    ),
    DirectTreasury: (
      <VStack w={'100%'}>
        <FormProvider {...formEdit}>
          <InputApplicationRange label="Investimento mínimo" />
        </FormProvider>
        <MultiSlider
          type="days"
          title="Escolha o vencimento"
          resetKey={resetKey}
          currentValue={daysRange}
          min={daysRangeDefault.min}
          max={maximumRedemptionDays}
          steps={1}
          onValueChange={(range) =>
            setDaysRange({ max: Number(range.max), min: Number(range.min) })
          }
        />
      </VStack>
    ),
  };

  const isDesktop = useBreakpointValue({
    base: false,
    lg: true,
  });

  const direction = isDesktop ? 'right' : 'bottom';

  const goBackButton = useBreakpointValue({
    base: (
      <IconButton
        icon="Close"
        testId={'button-close'}
        iconColor={Color['neutral.dark']}
        iconSize="xxs"
        onPress={goBack}
      />
    ),
    lg: (
      <Pressable testID={'button-close-filter-screen'} onPress={goBack}>
        <Icon
          testID={'icon-close-filter-screen'}
          name="Close"
          size="xs"
          variant="rounded"
          borderWidth={0.05}
          backgroundColor="transparent"
          color={Color['brand.primary.principal']}
          borderColor={Color['brand.primary.principal']}
        />
      </Pressable>
    ),
  });

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={goBack}
      width={sliderModalWidth}
      direction={direction}>
      <VStack maxHeight={{ base: height * 0.8, lg: 'full' }} flex={1}>
        <VStack
          alignItems={'center'}
          w={'full'}
          p={Spacing['inline.xxs']}
          borderTopRadius={{ base: Spacing['inline.xxxs'], sm: Spacing['inline.xxxs'], lg: 0 }}
          backgroundColor={Color['neutral.white']}
          space={Spacing['inline.xxxs']}>
          {!isDesktop && (
            <Box w="55px" background={'neutral.lightest'} borderRadius={BoxRadius.sm} h={1} />
          )}
          <HStack
            w={'full'}
            space={Spacing['stack.quarck']}
            alignItems="center"
            justifyContent={'space-between'}>
            <Text variant="semibold" fontSize={Typography['fontsize.lg']} lineHeight="distant">
              Escolha os filtros de pesquisa:
            </Text>
            {goBackButton}
          </HStack>
        </VStack>

        <ScrollView showsVerticalScrollIndicator={false} backgroundColor={Color['neutral.white']}>
          <VStack
            px={Spacing['inline.xxs']}
            pb={Spacing['inline.xxs']}
            maxWidth={{ base: '366px', lg: 'full' }}
            space={Spacing['stack.xxxs']}>
            {Object.keys(currentProductData).map((key) => {
              const isCategory = [
                'productSubCategory',
                'productCategory',
                'strategy',
                'direction',
                'indexer',
                'riskRatingLevel',
              ].includes(key);

              if (isCategory && params.checkbox.length === 0) {
                return;
              }

              const item = params?.checkbox?.find((itemValue) => itemValue?.field === key);

              const options = item?.options?.map((option: dataOptionsInterface) => ({
                ...option,
                value: `${item.field}:${option.value}`,
              }));

              const sideOptions = item?.sideOptions?.map((sideOption: dataOptionsInterface) => ({
                ...sideOption,
                value: `${item.field}:${sideOption.value}`,
              }));

              return (
                <Section
                  key={key}
                  {...currentProductData[key]}
                  {...(isCategory && item && item.options.length > 0 && { options })}
                  {...(isCategory && item && item.sideOptions.length > 0 && { sideOptions })}
                  onChange={handleSelectOption}
                  selectedValues={selectedValues}
                />
              );
            })}
            {sliderComponentsOrder[params.type]}
          </VStack>
        </ScrollView>
        <FormProvider {...formEdit}>
          <Footer
            buttonApplyIsDisabled={buttonApplyIsDisabled}
            buttonCleanIsDisabled={buttonCleanIsDisabled}
            handleApplyFilters={handleSubmit(handleApplyFilters)}
            handleCleanFilters={cleanFilters}
            currentApplicationRanges={currentApplicationRanges[params.type]}
          />
        </FormProvider>
      </VStack>
    </SliderModal>
  );
};
